import { createFileRoute } from '@tanstack/react-router'

export const Route = createFileRoute('/_site/privacy')({
  component: PrivacyPage,
  head: () => ({
    meta: [
      { title: 'Privacy Policy — How Uptrack Handles Your Data' },
      { name: 'description', content: 'How Uptrack handles your data: what we collect, where we store it, how long we keep it, and how to contact us with a privacy question.' },
      { property: 'og:title', content: 'Privacy Policy — How Uptrack Handles Your Data' },
      { property: 'og:description', content: 'How Uptrack handles your data. EU-hosted, no tracking cookies.' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/privacy' },
    ],
  }),
})

function PrivacyPage() {
  return (
    <>

      <div className="py-16">
        <div className="container mx-auto max-w-3xl px-4">
          <h1 className="mb-2 text-4xl font-semibold">Privacy Policy</h1>
          <p className="mb-12 text-muted-foreground">Last updated: October 9, 2026</p>

          <div className="space-y-10 text-muted-foreground">

            <section>
              <h2 className="mb-4 text-xl font-semibold text-foreground">What we collect</h2>
              <p className="mb-3">We collect the minimum data needed to run an uptime monitoring service:</p>
              <ul className="list-disc space-y-2 pl-6">
                <li><strong className="text-foreground">Account data</strong> — email address, name, organization name. Used for login, alerts, and billing.</li>
                <li><strong className="text-foreground">Monitor configuration</strong> — URLs, check intervals, alert settings, status page content. This is the service you're using us for.</li>
                <li><strong className="text-foreground">Check results</strong> — HTTP status codes, response times, error messages. Stored to show uptime history and power alerts.</li>
                <li><strong className="text-foreground">Usage data</strong> — page views, API calls, feature usage. Used to improve the product, not sold to anyone.</li>
                <li><strong className="text-foreground">Signup source</strong> — the campaign tags in the link you first arrived from (<code className="rounded bg-muted px-1.5 py-0.5 text-xs text-foreground">utm_source</code>, <code className="rounded bg-muted px-1.5 py-0.5 text-xs text-foreground">utm_medium</code>, <code className="rounded bg-muted px-1.5 py-0.5 text-xs text-foreground">utm_campaign</code>), the referring site (address without query string), and the first page you visited. Kept in your browser until you sign up, then stored with your account so we know which channels bring customers. No IP address, no cookies, no third parties.</li>
              </ul>
            </section>

            <section>
              <h2 className="mb-4 text-xl font-semibold text-foreground">Where we store it</h2>
              <p>
                All data is stored in <strong className="text-foreground">PostgreSQL databases on Netcup servers in Nuremberg, Germany</strong> (EU).
                We don't use US-based database providers. Backups are encrypted and stored on a separate server.
              </p>
            </section>

            <section>
              <h2 className="mb-4 text-xl font-semibold text-foreground">How long we keep it</h2>
              <p className="mb-3">Check result retention depends on your plan:</p>
              <ul className="list-disc space-y-2 pl-6">
                <li><strong className="text-foreground">Pro</strong> — 2 years</li>
                <li><strong className="text-foreground">Team</strong> — 2 years</li>
                <li><strong className="text-foreground">Business</strong> — 5 years</li>
              </ul>
              <p className="mt-3">
                Account data is kept as long as your account exists. When you delete your account, it is
                deactivated at once, and you can ask support to restore it for 30 days. After 30 days we
                permanently delete your account, your organization, your monitors, alert settings,
                incidents and check results. Encrypted database backups expire on a rolling schedule, so
                the deleted data leaves our backups within 35 more days. Invoices are kept by Paddle, our
                payment provider, as the law requires.
              </p>
            </section>

            <section>
              <h2 className="mb-4 text-xl font-semibold text-foreground">Third-party services</h2>
              <p className="mb-3">We use a small number of third-party services:</p>
              <ul className="list-disc space-y-2 pl-6">
                <li><strong className="text-foreground">Paddle</strong> — payment processing. They handle your credit card data directly; we never see it.</li>
                <li><strong className="text-foreground">Cloudflare</strong> — CDN and DDoS protection for the frontend. Standard web traffic data (IP, headers).</li>
              </ul>
              <p className="mt-3">Alert notifications and password resets are sent from our own mail server. We do not use a third-party email provider, so your email address stays with us.</p>
              <p className="mt-3">We do not sell data to anyone. We don't use Google Analytics or any ad-tracking pixels.</p>
            </section>

            <section>
              <h2 className="mb-4 text-xl font-semibold text-foreground">Cookies</h2>
              <p>
                We use a <strong className="text-foreground">single session cookie</strong> (<code className="rounded bg-muted px-1.5 py-0.5 text-xs text-foreground">_uptrack_key</code>) to keep you logged in.
                No tracking cookies, no third-party cookies, no cookie banner needed.
              </p>
            </section>

            <section>
              <h2 className="mb-4 text-xl font-semibold text-foreground">MCP / Claude Connector</h2>
              <p>
                If you connect Uptrack to Claude.ai via our MCP connector, Claude can access your
                monitor data, incidents, and analytics through OAuth 2.0. You authorize this explicitly,
                and you can revoke access anytime from Dashboard Settings &gt; Integrations.
                We don't share your data with Anthropic beyond what you explicitly request through Claude.
              </p>
            </section>

            <section>
              <h2 className="mb-4 text-xl font-semibold text-foreground">Your rights</h2>
              <p className="mb-3">You can:</p>
              <ul className="list-disc space-y-2 pl-6">
                <li><strong className="text-foreground">Export your data</strong> — available from Dashboard Settings</li>
                <li><strong className="text-foreground">Delete your account</strong> — permanently deletes all data after a 30-day restore period (backups within 35 more days)</li>
                <li><strong className="text-foreground">Revoke integrations</strong> — disconnect Claude or other OAuth clients anytime</li>
              </ul>
              <p className="mt-3">If you're in the EU, you have additional rights under GDPR (access, rectification, erasure, portability). Email us and we'll handle it.</p>
            </section>

            <section>
              <h2 className="mb-4 text-xl font-semibold text-foreground">Contact</h2>
              <p>
                Questions about privacy? Email{' '}
                <a href="mailto:support@uptrack.app" className="text-primary hover:underline">
                  support@uptrack.app
                </a>
              </p>
            </section>
          </div>
        </div>
      </div>

      </>
  )
}
